import{ useState, useEffect, useMemo, useCallback } from "react";
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import BG from "@/assets/optimized-images/maqrah2.jpg";

import ajeen from "@/assets/optimized-images/ajeen.png";
import muhammad from "@/assets/optimized-images/egbapics.jpg";
import Azzam from "@/assets/optimized-images/azzam.jpg";
import burhan from "@/assets/optimized-images/burhan.jpg";
import jafar from "@/assets/optimized-images/founder.png";
import Button from "@/components/shared/Button";
import ReviewModal from "./ReviewModal";

const Review = () => {
  const imageMap = useMemo(
    () => ({
      ajeen: ajeen,
      muhammad: muhammad,
      Azzam: Azzam,
      burhan: burhan,
      jafar: jafar,
    }),
    []
  );

  const [customers, setCustomers] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [fadeState, setFadeState] = useState("fade-in");
  const [showModal, setShowModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [editingReview, setEditingReview] = useState(null);

  const [formData, setFormData] = useState({
    name: "",
    country: "",
    comment: "",
    image: null,
    imagePreview: null,
  });

  useEffect(() => {
    const fetchReviews = async () => {
      try {
        const response = await fetch("/api/reviews");
        if (!response.ok) {
          throw new Error("Failed to fetch reviews");
        }
        const reviewsData = await response.json();

        const processedReviews = reviewsData.map((review) => ({
          ...review,
          image:
            imageMap[review.imageName] ||
            review.image ||
            "/api/placeholder/300/300",
        }));

        setCustomers(processedReviews);
        setIsLoading(false);
      } catch (error) {
        console.error("Error fetching reviews:", error);
        toast.error("Failed to load reviews");
        setIsLoading(false);
      }
    };

    fetchReviews();
  }, [imageMap]);

  useEffect(() => {
    if (customers.length === 0) return;

    const timer = setInterval(() => {
      setFadeState("fade-out");
      setTimeout(() => {
        setCurrentIndex((prevIndex) => (prevIndex + 1) % customers.length);
        setFadeState("fade-in");
      }, 300);
    }, 15000);

    return () => clearInterval(timer);
  }, [customers.length]);
  

  useEffect(() => {
    if (currentIndex >= customers.length && customers.length > 0) {
      setCurrentIndex(0);
    }
  }, [currentIndex, customers]);

  const handleNext = useCallback(() => {
    if (customers.length === 0) return;
    setFadeState("fade-out");

    setTimeout(() => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % customers.length);
      setFadeState("fade-in");
    }, 300);
  }, [customers.length]);

  useEffect(() => {
    if (customers.length === 0) return;

    const timer = setInterval(() => {
      handleNext();
    }, 15000);

    return () => clearInterval(timer);
  }, [customers.length, handleNext]);
  

  const handlePrev = () => {
    if (customers.length === 0) return;
    setFadeState("fade-out");

    setTimeout(() => {
      setCurrentIndex((prevIndex) =>
        prevIndex === 0 ? customers.length - 1 : prevIndex - 1
      );
      setFadeState("fade-in");
    }, 300);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData((prev) => ({
        ...prev,
        image: file,
        imagePreview: URL.createObjectURL(file),
      }));
    }
  };

  const startEditReview = (review) => {
    const currentUserName = review.name.split(", ")[0];
    const storedUserName = localStorage.getItem("userName");

    if (!storedUserName || currentUserName !== storedUserName) {
      toast.error("You can only edit your own review");
      return;
    }

    setEditingReview(review);
    setFormData({
      name: currentUserName,
      country: review.name.split(", ")[1],
      comment: review.comment,
      image: null,
      imagePreview: review.image,
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    if (!formData.name || !formData.country || !formData.comment) {
      toast.error("Please fill in all required fields");
      setIsSubmitting(false);
      return;
    }

    const today = new Date();
    const dateStr = `${today.getDate()} ${today.toLocaleString("default", {
      month: "long",
    })} ${today.getFullYear()}`;

    const submitData = new FormData();
    submitData.append("name", `${formData.name}, ${formData.country}`);
    submitData.append("country", formData.country);
    submitData.append("comment", formData.comment);
    submitData.append("date", dateStr);
    if (formData.image) {
      submitData.append("image", formData.image);
    }

    try {
      let response;
      let result;

      const backendUrl =
        import.meta.env.VITE_BACKEND_URL || "https://maqrahat.vercel.app/";
      if (editingReview) {
        response = await fetch(
          `${backendUrl}/api/reviews/${editingReview.id}`,
          {
            method: "PUT",
            body: submitData,
          }
        );
      } else {
        response = await fetch("/api/reviews", {
          method: "POST",
          body: submitData,
        });
      }

      if (!response.ok) {
        const errorText = await response.text();
        console.error("Error response:", errorText);
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      result = await response.json();

      if (result.errors) {
        toast.error(result.errors.join("\n"));
        return;
      }

      localStorage.setItem("userName", formData.name);

      const newReview = {
        id:
          result.id ||
          (editingReview ? editingReview.id : customers.length + 1),
        name: `${formData.name}, ${formData.country}`,
        comment: formData.comment,
        date: dateStr,
        image: formData.imagePreview || "/api/placeholder/300/300",
      };

      if (editingReview) {
        setCustomers((prev) =>
          prev.map((review) =>
            review.id === editingReview.id ? newReview : review
          )
        );
        toast.success("Review updated successfully");
      } else {
        setCustomers((prev) => [...prev, newReview]);
        setCurrentIndex(customers.length);
        toast.success("Review submitted successfully");
      }

      setFormData({
        name: "",
        country: "",
        comment: "",
        image: null,
        imagePreview: null,
      });
      setShowModal(false);
      setEditingReview(null);
    } catch (error) {
      console.error("Error submitting review:", error);
      toast.error(`Failed to submit your review: ${error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="w-full h-full text-white relative sm:h-screen md:h-screen lg:h-screen rounded-3xl text-center py-10 px-5 sm:px-16 bg-gray-900 flex items-center justify-center">
        <p>Loading reviews...</p>
        <ToastContainer />
      </div>
    );
  }

  if (customers.length === 0) {
    return (
      <div className="w-full h-full text-white relative sm:h-screen md:h-screen lg:h-screen rounded-3xl text-center py-10 px-5 sm:px-16 bg-gray-900">
        <div className="absolute inset-0 bg-black bg-opacity-80 rounded-3xl"></div>
        <div className="relative z-10 flex flex-col items-center justify-center h-full">
          <p>No reviews available. Be the first to leave a review!</p>
          <Button
            btnText={"Leave a review"}
            btnBgColor={"bg-blue-700 hover:bg-white"}
            btnColor={"text-white hover:text-blue-700"}
            padding={"py-3 px-5 rounded-md font-medium transition duration-300"}
            onClick={() => setShowModal(true)}
          />
        </div>
        {showModal && (
          <ReviewModal
            showModal={showModal}
            setShowModal={setShowModal}
            formData={formData}
            handleInputChange={handleInputChange}
            handleImageChange={handleImageChange}
            handleSubmit={handleSubmit}
            isSubmitting={isSubmitting}
            isEditing={!!editingReview}
          />
        )}
        <ToastContainer />
      </div>
    );
  }

  const currentCustomer = customers[currentIndex] || {
    name: "",
    comment: "",
    date: "",
    image: "/api/placeholder/300/300",
  };

  return (
    <div
      className="w-full h-full text-white relative sm:h-screen md:h-screen lg:h-screen rounded-3xl text-center sm:text-start py-10 px-2 sm:px-16 bg-gray-900"
      style={{ backgroundImage: `url(${BG})` }}
    >
      <div className="absolute inset-0 bg-black bg-opacity-80 rounded-3xl"></div>

      <div className="relative z-10 flex flex-col items-center justify-center h-full">
        <div
          className={`transition-opacity duration-300 ${
            fadeState === "fade-in" ? "opacity-100" : "opacity-0"
          }`}
        >
          <div className="flex flex-col sm:flex-row-reverse gap-y-8 sm:gap-y-0 sm:justify-between items-center sm:items-start w-full">
            <div className="overflow-hidden rounded-full w-32 h-32 sm:w-64 sm:h-64 flex-shrink-0 bg-blue-50 border-4 border-blue-400">
              <img
                src={currentCustomer.image || "/api/placeholder/300/300"}
                alt={currentCustomer.name || "Student"}
                className="w-full h-full object-cover"
              />
            </div>

            <div className="space-y-5 max-w-2xl">
              <div className="space-y-3">
                <h2 className="font-semibold text-lg sm:text-2xl text-blue-400">
                  {currentCustomer.name}
                </h2>
                <p className="sm:text-lg leading-relaxed">
                  {currentCustomer.comment || "No comment provided."}
                </p>
              </div>

              <div>
                <p className="font-semibold text-blue-400 mt-4">
                  {currentCustomer.date}
                </p>
              </div>

              <div className="sm:flex sm:flex-row-reverse sm:justify-between items-center mt-8">
                <div className="flex justify-center gap-x-6 mb-6 sm:mb-0">
                  <button
                    onClick={handlePrev}
                    aria-label="Previous testimonial"
                    className="focus:outline-none"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="48"
                      height="48"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="text-blue-400 hover:text-blue-300 transition-colors duration-300"
                    >
                      <circle cx="12" cy="12" r="10"></circle>
                      <polyline points="15 18 9 12 15 6"></polyline>
                    </svg>
                  </button>

                  <button
                    onClick={handleNext}
                    aria-label="Next testimonial"
                    className="focus:outline-none"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="48"
                      height="48"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="text-blue-400 hover:text-blue-300 transition-colors duration-300"
                    >
                      <circle cx="12" cy="12" r="10"></circle>
                      <polyline points="9 6 15 12 9 18"></polyline>
                    </svg>
                  </button>
                </div>

                <div className="flex justify-center items-center gap-4">
                  <Button
                    btnText={"Edit Review"}
                    btnBgColor={
                      "bg-green-600 sm:text-lg text-xs hover:bg-green-700"
                    }
                    btnColor={"text-white"}
                    padding={
                      "py-3 px-5 rounded-md font-medium transition duration-300"
                    }
                    onClick={() => startEditReview(currentCustomer)}
                  />
                  <Button
                    btnText={"Leave a review"}
                    btnBgColor={"bg-blue-700 sm:text-lg text-xs hover:bg-white"}
                    btnColor={"text-white hover:text-blue-700"}
                    padding={
                      "py-3 px-5 rounded-md font-medium transition duration-300"
                    }
                    onClick={() => setShowModal(true)}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-center mt-6">
          {customers.map((_, index) => (
            <button
              key={index}
              className={`mx-1 w-3 h-3 rounded-full ${
                index === currentIndex ? "bg-blue-400" : "bg-gray-400"
              }`}
              onClick={() => {
                setFadeState("fade-out");
                setTimeout(() => {
                  setCurrentIndex(index);
                  setFadeState("fade-in");
                }, 300);
              }}
              aria-label={`Go to testimonial ${index + 1}`}
            />
          ))}
        </div>
      </div>

      {showModal && (
        <ReviewModal
          showModal={showModal}
          setShowModal={setShowModal}
          formData={formData}
          handleInputChange={handleInputChange}
          handleImageChange={handleImageChange}
          handleSubmit={handleSubmit}
          isSubmitting={isSubmitting}
        />
      )}
    </div>
  );
};

export default Review;
